import React from 'react';

// Badge rarity levels and their styling
const BADGE_RARITY = {
  exclusive: {
    border: 'border-yellow-400',
    bg: 'bg-yellow-900/20',
    text: 'text-yellow-400',
    glow: 'shadow-lg shadow-yellow-400/20',
    label: 'EXCLUSIVE',
    gradient: 'from-yellow-400 to-yellow-600'
  },
  legendary: {
    border: 'border-purple-400',
    bg: 'bg-purple-900/20',
    text: 'text-purple-400',
    glow: 'shadow-lg shadow-purple-400/20',
    label: 'LEGENDARY',
    gradient: 'from-purple-400 to-purple-600'
  },
  rare: {
    border: 'border-blue-400',
    bg: 'bg-blue-900/20',
    text: 'text-blue-400',
    glow: 'shadow-lg shadow-blue-400/20',
    label: 'RARE',
    gradient: 'from-blue-400 to-blue-600'
  },
  common: {
    border: 'border-gray-400',
    bg: 'bg-gray-700/20',
    text: 'text-gray-400',
    glow: 'shadow-lg shadow-gray-400/20',
    label: 'COMMON',
    gradient: 'from-gray-400 to-gray-600'
  }
};

// Order badges by exclusivity and rarity
const getBadgeOrder = (achievements, getAchievementInfo) => {
  if (!achievements || achievements.length === 0) return [];
  
  return achievements
    .map(achievementId => {
      const achievement = getAchievementInfo(achievementId);
      if (!achievement) return null;
      
      // Determine rarity based on category and specific badges
      let rarity = 'common';
      if (achievement.category === 'exclusive') {
        rarity = 'exclusive';
      } else if (achievement.id.includes('legend') || achievement.id.includes('perfect') || achievement.id.includes('superstar') || achievement.id.includes('excellence')) {
        rarity = 'legendary';
      } else if (achievement.id.includes('veteran') || achievement.id.includes('master') || achievement.id.includes('expert') || achievement.id.includes('hub')) {
        rarity = 'rare';
      }
      
      return {
        ...achievement,
        rarity
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      // Sort by rarity first
      const rarityOrder = { exclusive: 0, legendary: 1, rare: 2, common: 3 };
      const rarityDiff = rarityOrder[a.rarity] - rarityOrder[b.rarity];
      if (rarityDiff !== 0) return rarityDiff;
      
      // Then sort alphabetically within same rarity
      return a.name.localeCompare(b.name);
    });
};

const BadgeDisplay = ({ achievements, getAchievementInfo, title = "Achievements", emptyMessage = "No achievements yet. Keep playing to earn badges!" }) => {
  const orderedBadges = getBadgeOrder(achievements, getAchievementInfo);

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <h2 className="text-xl font-bold text-white mb-4">{title}</h2>
      {orderedBadges.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {orderedBadges.map((badge, index) => {
            const rarityStyle = BADGE_RARITY[badge.rarity];
            
            return (
              <div
                key={index}
                className={`relative group cursor-pointer transition-all duration-300 hover:scale-110 ${rarityStyle.glow}`}
                title={`${badge.name} - ${badge.description}`}
              >
                {/* Badge Emblem */}
                <div className={`
                  w-16 h-16 mx-auto rounded-full border-2 ${rarityStyle.border} ${rarityStyle.bg}
                  flex items-center justify-center text-2xl
                  transition-all duration-300 hover:shadow-xl
                  bg-gradient-to-br ${rarityStyle.gradient} bg-opacity-10
                  relative overflow-hidden
                `}>
                  {/* Badge shine effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  
                  {/* Badge icon */}
                  <div className="relative z-10">
                    {badge.icon}
                  </div>
                  
                  {/* Badge border glow */}
                  <div className={`absolute inset-0 rounded-full border-2 ${rarityStyle.border} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}></div>
                </div>
                
                {/* Badge Name */}
                <div className="text-center mt-2">
                  <div className={`text-xs font-semibold ${rarityStyle.text} truncate px-1`}>
                    {badge.name}
                  </div>
                </div>
                
                {/* Rarity Label */}
                {badge.rarity === 'exclusive' && (
                  <div className="absolute -top-1 -right-1">
                    <div className={`text-xs px-1 py-0.5 rounded ${rarityStyle.bg} ${rarityStyle.text} font-bold border border-yellow-400/30`}>
                      {rarityStyle.label}
                    </div>
                  </div>
                )}
                
                {/* Tooltip on hover */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-10 border border-gray-700 shadow-xl">
                  <div className="font-semibold text-yellow-400">{badge.name}</div>
                  <div className="text-gray-300 mt-1">{badge.description}</div>
                  <div className="text-blue-400 mt-1 text-xs">{badge.requirement}</div>
                  <div className={`text-xs mt-1 ${rarityStyle.text}`}>{rarityStyle.label}</div>
                  
                  {/* Tooltip arrow */}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-400 text-sm">{emptyMessage}</p>
      )}
    </div>
  );
};

export default BadgeDisplay; 